import React, { useState, useRef, useEffect } from "react";
import { BarChart3, Building2, History, MapPin, PieChart, Power, Wifi } from "lucide-react";
import { ChevronDownIcon } from "../icons";
import {
  REPORT20_CLOSED_SITE_FINANCIALS_PAGE,
  REPORT20_DISCONNECTS_PAGE,
  REPORT20_MRC_HISTORY_PAGE,
  REPORT20_SITE_FOOTPRINT_PAGE,
  REPORT20_SPEND_OVERVIEW_PAGE,
  REPORT20_WIRELESS_USAGE_PAGE,
  REPORT20_YTD_BUDGET_PAGE,
  isReport20NavPage,
} from "../../config/report20Nav";

const LIVE_ITEMS = [
  { page: REPORT20_YTD_BUDGET_PAGE, label: "YTD Budget Tracking", icon: BarChart3 },
  { page: REPORT20_SPEND_OVERVIEW_PAGE, label: "Spend Overview", icon: PieChart },
  { page: REPORT20_MRC_HISTORY_PAGE, label: "MRC History", icon: History, hint: "Data only" },
  { page: REPORT20_DISCONNECTS_PAGE, label: "Disconnects", icon: Power },
];

const COMING_SOON_ITEMS = [
  { page: REPORT20_CLOSED_SITE_FINANCIALS_PAGE, label: "Closed Site Financials", icon: Building2 },
  { page: REPORT20_SITE_FOOTPRINT_PAGE, label: "Site Footprint Changes", icon: MapPin },
  { page: REPORT20_WIRELESS_USAGE_PAGE, label: "Wireless Usage", icon: Wifi },
];

const MenuItem = ({ page, label, icon: Icon, hint, currentPage, onClick }) => {
  const isCurrent = currentPage === page;
  return (
    <button
      type="button"
      role="menuitem"
      className={`w-full text-left flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium transition-colors ${
        isCurrent ? "bg-stone-100 text-[var(--ink,#1c1c1a)]" : "text-[var(--ink,#1c1c1a)] hover:bg-stone-50"
      }`}
      onClick={onClick}
    >
      <Icon className="w-4 h-4 shrink-0 text-[var(--muted,#6b6a66)]" strokeWidth={1.75} aria-hidden />
      <span className="min-w-0 flex-1 leading-snug">{label}</span>
      {hint ? (
        <span className="shrink-0 text-[11px] font-normal text-[var(--muted,#6b6a66)]">{hint}</span>
      ) : null}
    </button>
  );
};

/**
 * Pill trigger + chevron (mockup-style); main link opens full dashboard; menu items scroll to sections.
 */
const Report20NavGroup = ({ currentPage, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const go = (page) => (e) => {
    e.preventDefault();
    onNavigate(page);
    setOpen(false);
  };

  const active = isReport20NavPage(currentPage);

  return (
    <div className="relative report20-nav-group" ref={wrapRef}>
      <button
        type="button"
        className={`nav-item ${active ? "active" : ""}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        <span>Reports</span>
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-2 w-[260px] rounded-[12px] border border-[var(--line,#e4e2dc)] bg-white py-1.5 shadow-[0_12px_40px_rgba(28,28,26,0.16)] z-[120] text-left overflow-hidden"
          role="menu"
        >
          {LIVE_ITEMS.map((item) => (
            <MenuItem
              key={item.page}
              {...item}
              currentPage={currentPage}
              onClick={go(item.page)}
            />
          ))}
          <p className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--faint,#9a9a95)]">
            Coming soon
          </p>
          {COMING_SOON_ITEMS.map((item) => (
            <MenuItem
              key={item.page}
              {...item}
              currentPage={currentPage}
              onClick={go(item.page)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Report20NavGroup;
